import { defineNuxtPlugin } from '#imports'

import { getRequestLocale } from '@backmarket/nuxt-module-i18n-locale/getRequestLocale'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n-locale/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { isTestEnv } from '@backmarket/utils/env/isTestEnv'

import { useInjectTranslationsScriptInHead } from './composables/useInjectTranslationsScriptInHead'
import { useShouldLogMissingTranslationValue } from './composables/useShouldLogMissingTranslationValue'
import { useShowDefaultMessage } from './composables/useShowDefaultMessage'
import { useShowTranslationKeys } from './composables/useShowTranslationKeys'
import { PROCESS_KEY } from './constants'
import type { I18n } from './types/i18n'
import { getI18n } from './utils/getI18n'

export type Injections = {
  i18n: I18n
}

export default defineNuxtPlugin<Injections>({
  name: 'i18n',
  dependsOn: ['i18nLocale'],
  setup: (nuxt) => {
    if (nuxt.ssrContext) {
      const locale = getRequestLocale(nuxt.ssrContext.event)

      if (!globalThis[PROCESS_KEY]) {
        throw new Error(
          '[nuxt-module-i18n] Cannot retrieve translations from the main process',
        )
      }

      if (import.meta.server) {
        useInjectTranslationsScriptInHead(locale)
      }
    }

    if (!isTestEnv() && !useI18nLocale()) {
      throw new Error('[nuxt-module-i18n] Cannot retrieve locale in useState')
    }

    const locale = useI18nLocale()
    const { market } = useMarketplace()
    const currencyCode = market.defaultCurrency
    const showDefaultMessage = useShowDefaultMessage()
    const showTranslationKeys = useShowTranslationKeys()
    const shouldLogMissingTranslationValue =
      useShouldLogMissingTranslationValue()

    return {
      provide: {
        i18n: getI18n(
          locale,
          currencyCode,
          showTranslationKeys,
          showDefaultMessage,
          shouldLogMissingTranslationValue,
        ),
      },
    }
  },
})
